import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSettingsQuery } from "../lib/settings";
import { DEFAULT_VOICE } from "../lib/voice";
import { VoiceMode } from "./VoiceMode";

interface Msg {
  direction: string;
  body: string;
}

/** A voice conversation with SOMEONE ELSE's assistant (it calls you, speaks first, listens, answers). */
export function AssistantCall({ threadId, onClose }: { threadId: string; onClose: () => void }) {
  const t = useT();
  const settings = useSettingsQuery(true);
  const detail = useQuery({
    queryKey: ["call", threadId],
    queryFn: () =>
      api<{ messages: Msg[]; lang?: string; gender?: "female" | "male" }>(
        `/assistant/inbox/${threadId}`,
      ),
  });
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  if (!detail.data) return null;
  const greeting = detail.data.messages.find((m) => m.direction === "out")?.body;
  return (
    <VoiceMode
      cfg={settings.data?.settings.voice ?? DEFAULT_VOICE}
      title={t("assistant.callTitle")}
      greeting={greeting}
      persona={{ lang: detail.data.lang ?? "ar", gender: detail.data.gender }}
      assistantText={text}
      assistantRunning={running}
      onStopRun={() => undefined}
      onClose={onClose}
      onSend={(msg) => {
        setText("");
        setRunning(true);
        void api<{ reply?: string; done: boolean }>(`/assistant/inbox/${threadId}/reply`, {
          method: "POST",
          body: { message: msg },
        })
          .then((r) => {
            setText(r.reply ?? "");
            // The conversation reached its goal: hang up after the closing words have been spoken.
            if (r.done) setTimeout(onClose, 5000 + (r.reply?.length ?? 0) * 70);
          })
          .catch(() => setText(""))
          .finally(() => setRunning(false));
      }}
    />
  );
}
